import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-bold ring-offset-background transition-[background-color,border-color,color,transform] duration-150 ease-out-strong active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 max-w-full',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary-dark hover:border-primary-dark',
        'primary-light': 'bg-primary-light text-primary-foreground hover:bg-primary hover:border-primary',
        destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'text-primary border border-solid border-primary-lightest bg-white hover:bg-primary hover:text-white hover:border-primary',
        'outline-dashed': 'bg-none border border-dashed border-neutral text-neutral hover:text-neutral-light hover:border-neutral-light',
        'outline-success': 'bg-none border border-solid border-success text-success hover:bg-success-lightest',
        'outline-danger': 'bg-none border border-solid border-danger text-danger hover:bg-danger-lightest',
        'outline-third': 'text-third border border-solid border-third-light bg-white hover:bg-third hover:text-white hover:border-third',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        third: 'bg-third text-white hover:bg-third-light',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary hover:text-primary-dark underline-offset-2 underline',
        mission: 'bg-mission text-primary-foreground hover:bg-mission-light hover:border-mission-light hover:text-mission',
        merit: 'bg-merit text-primary-foreground hover:bg-merit-light hover:border-merit-light hover:text-merit',
        done: 'bg-done text-success cursor-default !px-8',
        success: 'bg-success hover:bg-success-light border border-solid border-success hover:border-success-light text-white',
        dark: 'bg-neutral text-white hover:bg-neutral-dark',
        none: 'appearance-none bg-transparent border-none p-0 text-left text-primary-dark'
      },
      size: {
        default: 'h-10 px-4 py-2',
        xxs: 'h-5 px-2',
        xs: 'h-6 px-2 text-xs',
        sm: 'h-9 px-3',
        md: 'h-11 px-3',
        lg: 'h-12 px-6 text-base',
        icon: 'h-10 w-10 shrink-0'
      },
      fullWidth: {
        true: 'w-full',
        false: ''
      },
      wide: {
        true: 'px-4 md:px-12'
      },
      border: {
        true: 'border border-solid border-white border-2 shadow-dropshadow-md'
      },
      isAlignedWithTabs: {
        true: 'md:absolute md:top-[105px] md:right-[45px]'
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      fullWidth: false,
      wide: false,
      border: false,
      isAlignedWithTabs: false
    }
  }
);

const Button = React.forwardRef(({ className, variant, size, fullWidth, wide, border, isAlignedWithTabs, asChild = false, disabled, ...props }, ref) => {
  const Comp = asChild && !disabled ? Slot : 'button';

  return (
    <Comp
      className={cn('c-button', buttonVariants({ variant, size, fullWidth, wide, border, isAlignedWithTabs, className }))}
      ref={ref}
      disabled={disabled}
      {...(asChild && disabled ? { href: undefined, 'aria-disabled': true, tabIndex: -1 } : {})}
      {...props}
    />
  );
});
Button.displayName = 'Button';

const buttonIconVariants = cva('inline-flex relative shrink-0 flex-col [&>svg]:h-auto', {
  variants: {
    size: {
      default: '[&>svg]:w-4',
      md: '[&>svg]:w-5'
    }
  },
  defaultVariants: {
    size: 'default'
  }
});

const ButtonIcon = React.forwardRef(({ className, size, ...props }, ref) => (
  <span
    className={cn('c-button-icon', buttonIconVariants({ size }), className)}
    ref={ref}
    {...props}
  />
));
ButtonIcon.displayName = 'ButtonIcon';

const ButtonText = React.forwardRef(({ className, ...props }, ref) => (
  <span
    className={cn('truncate leading-none', className)}
    ref={ref}
    {...props}
  />
));
ButtonText.displayName = 'ButtonText';

const buttonBackVariants = cva(
  [
    'appearance-none rounded-none border-none bg-transparent p-0 transition-[opacity,box-shadow] duration-150 ease-out-strong',
    'flex min-h-8 max-w-full cursor-pointer items-center gap-3 text-left leading-tight',
    '[&>.c-icon-wrapper]:hover:shadow-[0px_4px_10px_rgba(68,43,140,0.2)]'
  ],
  {
    variants: {
      disabled: {
        true: 'opacity-50'
      }
    },
    defaultVariants: {
      disabled: false
    }
  }
);

const ButtonBack = React.forwardRef(({ className, disabled, asChild, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      ref={ref}
      className={cn('c-button-back', buttonBackVariants({ disabled }), className)}
      {...props}
    />
  );
});
ButtonBack.displayName = 'ButtonBack';

const ButtonBackContent = React.forwardRef(({ className, disabled, ...props }, ref) => (
  <span
    ref={ref}
    className={cn('c-button-back-content', 'flex min-w-10 shrink grow flex-col gap-1', className)}
    {...props}
  />
));
ButtonBackContent.displayName = 'ButtonBackContent';

export { Button, buttonVariants, ButtonIcon, ButtonText, ButtonBack, ButtonBackContent };
